import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Clock, Ban, Shield, FileText } from "lucide-react";
import { SALON_POLICIES } from "@/data/policies";

const policyIcons = {
  arrival: Clock,
  cancellation: Ban,
  "squeeze-ins": FileText,
  payments: FileText,
  security: Shield,
  "hair-prep": FileText,
};

export default function PoliciesPage() {
  const [openPolicy, setOpenPolicy] = useState(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    revealRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  const togglePolicy = (id) => {
    setOpenPolicy(openPolicy === id ? null : id);
  };

  return (
    <div className="bg-[#F0F0F0] min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="script-heading text-3xl md:text-4xl mb-4">
            Salon Policies
          </h1>
          <p className="text-[#666] max-w-lg mx-auto">
            Please review our policies to ensure a smooth and enjoyable experience.
          </p>
        </div>
      </section>

      {/* Policies List */}
      <section 
        data-testid="policies-section" 
        className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
      >
        <div className="space-y-3">
          {SALON_POLICIES.map((policy, index) => {
            const PolicyIcon = policyIcons[policy.id] || FileText;
            return (
            <div
              key={policy.id}
              ref={addRevealRef}
              data-testid={`policy-${policy.id}`}
              className="reveal"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <button
                data-testid={`policy-btn-${policy.id}`}
                onClick={() => togglePolicy(policy.id)}
                className={`w-full policy-btn justify-between ${
                  openPolicy === policy.id ? "rounded-b-none" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="policy-btn-icon">
                    <PolicyIcon size={14} />
                  </span>
                  <span>{policy.title}</span>
                </div>
                <ChevronDown
                  className={`transition-transform duration-300 ${
                    openPolicy === policy.id ? "rotate-180" : ""
                  }`}
                  size={18}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 bg-white rounded-b-2xl ${
                  openPolicy === policy.id ? "max-h-[900px]" : "max-h-0"
                }`}
              >
                <div className="p-6">
                  <p className="text-[#666] text-sm whitespace-pre-line leading-relaxed">
                    {policy.content}
                  </p>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Contact Note */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="glass-card p-6 text-center">
          <p className="text-[#666] text-sm">
            Have questions about my policies?{" "}
            <Link
              to="/contact"
              className="text-[#111] underline underline-offset-4 hover:text-[#666]"
            >
              Contact me
            </Link>{" "}
            and I'll be happy to help.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h2 className="script-heading text-2xl mb-4">
            Ready to Book?
          </h2>
          <p className="text-[#666] mb-6">
            Now that you're familiar with our policies, book your appointment.
          </p>
          <Link
            to="/book"
            data-testid="policies-book-btn"
            className="btn-black inline-block"
          >
            Book Appointment
          </Link>
        </div>
      </section>
    </div>
  );
}
